import { Message } from '@patsnap/tahiti-core/common/message';
import { isPsHttpError } from '@patsnap/tahiti-core/common/ps-http';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ErrorHandlerContribution, ErrorHandlerRegistry } from '@patsnap/tahiti-core/common/error-handler-contribution';
import { I18nService } from '@/services/i18n-service';
import { HttpSuccessException } from '@eureka/services/src/exception';
import {
    isUsageAmountWhere,
    prepareInterceptorEdition,
    UsageAmountWhere,
} from '@eureka/shared/edition-upgrade/src/constants';

function getAgenticUsageAmountWhere(): UsageAmountWhere {
    const hashQuery = window.location.hash.split('?')[1] ?? '';
    const params = new URLSearchParams(hashQuery || window.location.search);
    const routeType = params.get('type');
    return isUsageAmountWhere(routeType) ? routeType : UsageAmountWhere.TRIZ;
}

@injectable()
export class AirErrorHandler implements ErrorHandlerContribution {
    constructor(
        @inject(Message)
        private readonly _message: Message,
        @inject(I18nService)
        private readonly _i18nService: I18nService
    ) { }

    private _showErrorMessageByCode(code: number) {
        const key = `commonStatusCode.${code || '400'}`;
        const message = this._i18nService.te(key) ? this._i18nService.t(key) : this._i18nService.t('commonStatusCode.400');
        if (message) {
            this._message.error({
                message,
                dangerouslyUseHTMLString: true,
            });
            return true;
        }
        return false;
    }

    registerHandler(handlers: ErrorHandlerRegistry): void {
        handlers.register((error: any) => {
            if (error?.response?.status === 403) {
                this._showErrorMessageByCode(403);
                return true;
            }
            if (error.cause?.error_code === 90164080) {
                this._message.error({
                    message: error.cause.error_msg,
                    dangerouslyUseHTMLString: true,
                });
                return true;
            }
            return false;
        });

        handlers.register((error: any) => {
            if (prepareInterceptorEdition(error?.cause?.error_code || error?.data?.error_code)) {
                import('@eureka/shared/edition-upgrade').then(({ invokeEditionUpgrade }) => {
                    invokeEditionUpgrade(getAgenticUsageAmountWhere()).then((needToast) => {
                        needToast && this._showErrorMessageByCode(error?.cause?.error_code || error?.data?.error_code);
                    });
                });
                return true;
            }
            return false;
        });

        handlers.register((error: Error) => {
            if (!HttpSuccessException.is(error)) {
                return false;
            }
            const data = error.data;
            return this._showErrorMessageByCode(data.error_code);
        });

        handlers.register((error: Error) => {
            if (!isPsHttpError(error)) {
                console.error('error', error);
                return false;
            }
            const data = error.cause;
            return this._showErrorMessageByCode(data.error_code);
        });
    }
}
